'use client';

import { Suspense } from 'react';
import type { AuthConfig } from '@sso/auth-client';

import { GradientLayer } from '@workspace/lib/components/atoms/GradientLayer';
import { ModalPopupWarningIcon } from '@workspace/lib/components/atoms/icons/ModalPopupWarningIcon';
import { Navbar } from '@workspace/lib/components/organisms/Navbar';
import { cn } from '@workspace/lib/utils/cn';

export interface PageLayoutProps {
  logo: React.ReactNode;
  navbarPrimaryText?: string | null;
  navbarSecondaryText?: string | null;
  customerService?: React.ReactNode;
  isMobileDevice: boolean;
  children: React.ReactNode;
  progressBar?: React.ReactNode;
  /** @deprecated Locale switch moved to FormPage. Prop kept for RO backward compat. */
  localeSwitch?: React.ReactNode;
  isThankYouPageGradientLayerShown?: boolean;
  isOfferPageGradientLayerShown?: boolean;
  isReferralSuccessGradientShown?: boolean;
  isOfferOrThankYouPage?: boolean;
  isBrokenJourneyPage?: boolean;
  isFullWidthPage?: boolean;
  logoutButtonText?: string | null;
  phoneNumber?: string | null;
  compactPhoneNumber?: string | null;
  authConfig?: AuthConfig | null;
  logoutReturnUrl?: string | null;
  profile?: React.ReactNode;
  isTechnicalErrorPage?: boolean;
}

const PageHeader = ({
  isTechnicalErrorPage,
  progressBar,
  customerService,
}: Pick<
  PageLayoutProps,
  'isTechnicalErrorPage' | 'customerService' | 'progressBar'
>) => {
  const getLeftContent = () => {
    if (isTechnicalErrorPage) {
      return (
        <div className='text-warning flex w-full items-center justify-center md:max-w-180 lg:max-w-130'>
          <ModalPopupWarningIcon width={60} height={60} />
        </div>
      );
    }

    if (progressBar) {
      return <Suspense fallback={null}>{progressBar}</Suspense>;
    }

    return null;
  };

  const leftContent = getLeftContent();

  return (
    leftContent && (
      <div className='flex w-full items-start md:mt-4 md:justify-center lg:justify-start lg:gap-6'>
        {leftContent}
        {customerService ? (
          <div className='hidden lg:block lg:h-[60px]'>{customerService}</div>
        ) : null}
      </div>
    )
  );
};

export function PageLayout({
  logo,
  navbarPrimaryText,
  navbarSecondaryText,
  customerService,
  isMobileDevice,
  isOfferOrThankYouPage,
  isFullWidthPage,
  children,
  progressBar,
  localeSwitch: _localeSwitch,
  isThankYouPageGradientLayerShown,
  isBrokenJourneyPage,
  isOfferPageGradientLayerShown,
  isReferralSuccessGradientShown,
  logoutButtonText,
  phoneNumber,
  compactPhoneNumber,
  authConfig,
  logoutReturnUrl,
  profile,
  isTechnicalErrorPage,
}: PageLayoutProps) {
  return (
    <div
      className={cn('bg-white-primary relative min-h-screen w-full', {
        'sm:bg-[linear-gradient(180deg,#e6f8fe_0%,#ffffff_30%)]':
          isOfferPageGradientLayerShown,
        'bg-[linear-gradient(180deg,#FFFFFF_0%,#E9F8F5_35%,#D3F1F0_65%,#BEEAEB_100%)]':
          isReferralSuccessGradientShown,
      })}
    >
      <Navbar
        Logo={logo}
        primaryText={navbarPrimaryText}
        secondaryText={navbarSecondaryText}
        isOfferOrThankYouPage={isOfferOrThankYouPage}
        logoutButtonText={logoutButtonText}
        phoneNumber={phoneNumber}
        compactPhoneNumber={compactPhoneNumber}
        authConfig={authConfig}
        logoutReturnUrl={logoutReturnUrl}
        profile={profile}
      />

      {isFullWidthPage ? (
        <main className='relative z-20 w-full'>
          {isOfferPageGradientLayerShown && (
            <GradientLayer className='bg-provi-offer-gradient top-0 left-1/2 -z-10 w-screen -translate-x-1/2' />
          )}
          {children}
        </main>
      ) : (
        <main className='relative mx-auto max-w-5xl px-4 pt-0 pb-4 sm:px-6 md:px-8 lg:px-12'>
          <PageHeader
            isTechnicalErrorPage={isTechnicalErrorPage}
            customerService={customerService}
            progressBar={progressBar}
          />
          <div className='relative z-20 w-full'>
            <div className='flex gap-8'>
              <div
                className={cn(
                  'flex w-full flex-1 flex-col items-center md:w-auto',
                  {
                    'lg:items-start': !isBrokenJourneyPage,
                  },
                )}
              >
                <div
                  className={cn('w-full', {
                    'max-w-full': isMobileDevice,
                    'sm:max-w-3xl lg:max-w-2xl': !isMobileDevice,
                  })}
                >
                  {children}
                </div>
              </div>
            </div>
          </div>
        </main>
      )}
      {isThankYouPageGradientLayerShown && (
        <GradientLayer
          isMobileDevice={isMobileDevice}
          className='bg-provi-brand-gradient bottom-0 z-10'
        />
      )}
    </div>
  );
}
